// libs
import { useState, useEffect, useRef, useCallback, ChangeEvent } from 'react';
// components
import UploadingQueueLayout from '../../layout/list/UploadingQueueLayout';
// services
import useUploadingQueueService from './uploadingQueueService';
import { useFormContext } from 'framework/form';
// static
import fileUploadImg from '../../../images/upload.svg';
import { mapExtensions, usePasteFromBuffer } from 'framework/utils';
import { Box, Typography } from '@mui/material';

export type UploadFileParams = {
  url: string;
  data: FormData;
  eventTarget?: AbortSignal;
  onProgress?: (progress: { loaded: number; total: number }) => void;
};

export type UploadFileResponse = {
  response: string;
};

type BaseFileUploaderProps = {
  validationCallback: (error: any) => void;
  validateFunc: (file: File[]) => any;
  uploadCallback: (id: string, file: File) => void;
  acceptExtensions: Nullable<string>;
  artifactTypeId: string;
  source: string;
  canAddFromClipboard?: boolean;
  showBtnImage?: boolean;
  uploadBtnText?: string;
  disabled?: boolean;
  uploadFile: (params: {
    file: File;
    artifactTypeId: string | number | Blob;
    signal?: AbortSignal;
    onProgress?: (progress: { loaded: number; total: number }) => void;
  }) => Promise<string | null>;
  maxElements?: number;
};

const BaseFileUploader = ({
  validationCallback = () => {},
  validateFunc = () => {},
  uploadCallback = () => {},
  acceptExtensions = null,
  artifactTypeId,
  source,
  canAddFromClipboard = false,
  showBtnImage = true,
  uploadBtnText = 'Upload or drag files',
  disabled = false,
  uploadFile,
  maxElements,
}: BaseFileUploaderProps) => {
  const { addToUploadingQueue, removeFromUploadingQueue, modifyLoadingProgress, getObjectsInQueue } =
    useUploadingQueueService();

  const { setIsLoadingArtifact = () => {} } = useFormContext();

  const [uploadCount, _setUploadCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragEnter, setDragEnter] = useState(false);
  const countRef = useRef(uploadCount);

  const setUploadCount = useCallback(
    (data: (arg0: number) => any) => {
      const result = typeof data === 'function' ? data(countRef.current) : data;
      countRef.current = result;
      _setUploadCount(result);
    },
    [_setUploadCount],
  );

  const upload = async (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const { files } = e.target;

    if (!files) {
      return;
    }

    const fileArray = Array.from(files);

    const error = validateFunc([...fileArray, ...getObjectsInQueue()]);

    if (error) {
      validationCallback(error);
      e.preventDefault();
      return;
    }

    const promises = Array.from(files).map(async file => {
      const { temporaryId, signal } = await addToUploadingQueue(file);

      const artifactId = await uploadFile({
        file,
        signal,
        artifactTypeId,
        onProgress: (loadingProgress: any) => {
          modifyLoadingProgress(temporaryId, loadingProgress);
        },
      });

      removeFromUploadingQueue(temporaryId);
      uploadCallback(artifactId || '', file);
    });

    setUploadCount(currentCount => currentCount + 1);

    Promise.all(promises.map(p => p.catch(er => er))).finally(() => {
      setUploadCount(currentCount => currentCount - 1);
    });

    input.value = '';
  };

  useEffect(() => {
    if (uploadCount === 0) {
      setIsLoadingArtifact(false);
      setIsUploading(false);
    } else {
      setIsLoadingArtifact(true);
      setIsUploading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadCount]);

  useEffect(() => {
    return () => {
      setIsLoadingArtifact(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteFile = (id: string) => {
    removeFromUploadingQueue(id);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDragEnter(false);
    validationCallback(null);
    upload(e);
  };

  const handleDragEnter = () => {
    setDragEnter(true);
  };

  const handleDragLeave = () => {
    setDragEnter(false);
  };

  const handlePaste = useCallback(
    ({ clipboardData: { files } }: any) => {
      if (countRef.current === 0 && files.length) {
        handleChange({ target: { files } } as ChangeEvent<HTMLInputElement>);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [countRef.current, validateFunc],
  );

  usePasteFromBuffer(handlePaste, canAddFromClipboard);

  const shouldShowUploader = !(isUploading && maxElements === 1);

  return (
    <>
      <UploadingQueueLayout
        loadingArtifactObjects={getObjectsInQueue()}
        onRemove={loadingArtifactObjects => deleteFile(loadingArtifactObjects.id)}
      />
      {shouldShowUploader && (
        <Box
          component="label"
          className="photo-upload"
          title={canAddFromClipboard ? 'Copy screenshot to clipboard and perform hotkey paste(Ctrl/Cmd + V)' : ''}
          htmlFor={source}
          data-testid="dropzone-label"
          data-drag-active={isDragEnter || undefined}
          sx={theme => ({
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100px',
            height: '100px',
            backgroundColor: disabled ? 'border.medium' : 'background.default',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'box-shadow 0.15s ease-in-out',
            [theme.breakpoints.down('smallMobile')]: {
              width: '50px',
              height: '50px',
            },
            [theme.breakpoints.up('sm')]: !disabled
              ? {
                  ...(isDragEnter && { boxShadow: '0 0 0 2px var(--mui-palette-primary-main)' }),
                  '&:hover': { boxShadow: '0 0 0 2px var(--mui-palette-primary-main)' },
                }
              : {},
            '& input': {
              cursor: 'inherit',
              display: 'block',
              position: 'absolute',
              inset: 0,
              opacity: 0,
            },
          })}
        >
          <input
            id={source}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            name={source}
            disabled={disabled}
            type="file"
            onChange={handleChange}
            accept={mapExtensions(acceptExtensions) as string}
            multiple
            data-testid="file-input"
          />
          {showBtnImage && <img src={fileUploadImg} alt="Upload" />}
          <Typography
            component="span"
            variant="inherit"
            sx={theme => ({
              fontSize: '12px',
              marginTop: '5px',
              color: 'text.primary',
              textAlign: 'center',
              [theme.breakpoints.down('smallMobile')]: { display: 'none' },
            })}
          >
            {uploadBtnText}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default BaseFileUploader;
